import type { ApiErrorModel, BuyAddonsModel } from '@core/base/common/models/api.models';
import type { UpsalePurchase } from '../interfaces/upsalePurchase.interface';
import { usePaymentError } from './usePaymentError';

export function payments() {
  const { setLoader } = useDialogStore();
  const currentPaymentMethod = ref<number>(1); //  1 - card or 2 - paypal
  const API = useKegelApi();
  const { $paymentMethods } = useNuxtApp();
  const { reportPaymentError } = usePaymentError();
  const device = useDevice();
  const { isTikTokInApp, isSnapchatInApp } = useHelpers();
  const { sendGtmPurchase } = useCustomGtag();
  const isPayPalAvailable = ref(true);
  const isApplePayAvailable = ref(true);

  const isShowApplePay = computed((): boolean => {
    if (import.meta.client) {
      return Boolean((globalThis as any).ApplePaySession?.canMakePayments())
        && currentPaymentMethod.value === 1;
    }

    return false;
  });

  const isPayPalBlockedInApp = computed(
    () => (isTikTokInApp.value || isSnapchatInApp.value) && device.isIos,
  );

  const isShowPayPal = computed(() => isPayPalAvailable.value && !isPayPalBlockedInApp.value);

  const paymentMethods = computed(() => {
    const card = { id: 1, imageUrl: 'all_cards.webp' };

    return isShowPayPal.value
      ? [{ id: 0, imageUrl: 'icon_paypal.webp' }, card]
      : [card];
  });

  function setPayPalAvailable(available: boolean): void {
    isPayPalAvailable.value = available;

    if (!available && currentPaymentMethod.value === 0) {
      currentPaymentMethod.value = 1;
    }
  }

  function setApplePayAvailable(available: boolean): void {
    isApplePayAvailable.value = available;
  }

  const isShowGpay = computed((): boolean => {
    return !isShowApplePay.value && (currentPaymentMethod.value === 1 || currentPaymentMethod.value === 4) && !isTikTokInApp.value && !device.isIos;
  });

  async function buyUpsale({ intent_id, paypal_token_id, stripe_temp_token, item, web_user_uuid, stage, amount }: UpsalePurchase) {
    setLoader(true);
    try {
      const [paymentField, paymentValue] = Object.entries({
        stripe_temp_token,
        paypal_token_id,
        intent_id,
      }).find(([, value]) => value) ?? [];

      const payload: BuyAddonsModel = {
        item,
        web_user_uuid,
        stage,
        amount,
        ...(paymentField ? { [paymentField]: paymentValue } : {}),
      };

      const response = await API.buyAddons(payload);

      if (response?.detail?.error) {
        throw response?.detail.error;
      } else {
        sendGtmPurchase(amount as number);
        setLoader(false);
      }
    } catch (error) {
      return error;
    }
  }

  async function buyCardUpsale(purchase: UpsalePurchase): Promise<void> {
    const error = await buyUpsale(purchase);

    if (error) {
      reportPaymentError({
        error: error as ApiErrorModel,
        stage: 'Upsale',
        type: $paymentMethods.card,
      });
    }
  }

  return {
    buyUpsale,
    buyCardUpsale,
    isShowPayPal,
    isPayPalBlockedInApp,
    setPayPalAvailable,
    isShowApplePay,
    isApplePayAvailable,
    setApplePayAvailable,
    currentPaymentMethod,
    isShowGpay,
    paymentMethods,
  };
}
